import {
  RollbackOutlined,
  MinusSquareFilled,
  TableOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Input,
  message,
  Popover,
  Radio,
  Select,
  Space,
  Table,
  Tabs,
  Typography,
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Outlet, useSearchParams } from 'react-router-dom';
import countries from 'world_countries_lists/data/countries/pt/countries.json';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQueries from '../../api/useAPIQueries';
import useAPIQuery from '../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../api/useInfiniteAPIQuery';
import ActivateApplicantModal from '../../components/ActivateApplicantModal';
import AddCandidateDrawer from '../../components/AddCandidateDrawer';
import ApplicantAnnotationModal from '../../components/ApplicantAnnotationModal';
import BulkMoveCandidateStageModal from '../../components/BulkMoveCandidateStageModal';
import BulkRejectCandidateModal from '../../components/BulkRejectCandidateModal';
import BulkSelectCandidateModal from '../../components/BulkSelectCandidateModal';
import BulkSendAdditionalQuestionsModal from '../../components/BulkSendAdditionalQuestionsModal';
import BulkSendCustomTestsModal from '../../components/BulkSendCustomTestsModal';
import BulkSendEmailModal from '../../components/BulkSendEmailModal';
import BulkSendPracticalTechnicalCase from '../../components/BulkSendPracticalTechincalCaseModal';
import BulkSendTestsModal from '../../components/BulkSendTestsModal';
import CandidateTable from '../../components/CandidateTable';
import ErrorResult from '../../components/ErrorResult';
import Filter from '../../components/Filter';
import { deleteScoreSearchParams } from '../../components/Filter/Utils/deleteScoreSearchParams';
import GiveUpApplicantModal from '../../components/GiveUpApplicantModal';
import OpportunityTabs from '../../components/OpportunityTabs';
import PageContent from '../../components/PageContent';
import PartialMoveCandidateStageModal from '../../components/PartialMoveCandidateStageModal';
import PartialRejectCandidateModal from '../../components/PartialRejectCandidateModal';
import PartialSelectCandidateModal from '../../components/PartialSelectCandidateModal';
import PartialSendAdditionalQuestionsModal from '../../components/PartialSendAdditionalQuestionsModal';
import PartialSendCustomTestsModal from '../../components/PartialSendCustomTestsModal';
import PartialSendEmailModal from '../../components/PartialSendEmailModal';
import PartialSendPracticalTechnicalCaseModal from '../../components/PartialSendPracticalTechnicalCaseModal';
import PartialSendTestsModal from '../../components/PartialSendTestsModal';
import RangeFilterTag from '../../components/RangeFilterTag';
import SalaryRangeFilterTag from '../../components/SalaryRangeFilterTag';
import { SearchAttachmentContent } from '../../components/SearchEngine/SearchAttachmentContent';
import SearchParam from '../../components/SearchParam';
import SendSankhyaAdmissionModal from '../../components/SendSankhyaAdmissionModal';
import SendVixtingAdmissionModal from '../../components/SendVixtingAdmissionModal';
import useCanAccessJobPosting from '../../hooks/useCanAccessJobPosting';
import useCreateInterviewMutation from '../../hooks/useCreateInterviewMutation';
import usePrefetchPages from '../../hooks/usePrefetchPages';
import { SearchCandidate } from '../../hooks/useSearchCandidates';
import useSearchCandidatesController from '../../hooks/useSearchCandidatesController';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import {
  RecruitmentProcessCollection,
  MindsightFunction,
  PaginatedResponse,
  CandidateCollection,
  JobPostingId,
  CandidateId,
  JobPostingDetail,
} from '../../types';
import ApplicantAnalysisBulkActions from './ApplicantAnalysisBulkActions';
import ApplicantAnalysisTableActions from './ApplicantAnalysisTableActions';
import StageAggregateTitle from './StageAggregateTitle';
import styles from './index.module.css';

const gradeColumnOptions = [
  'TAGS',
  'MATCH',
  'RAW_POTENTIAL',
  'OCP',
  'LOGIC',
  'SJT',
  'MOTIVATIONAL',
  'OPERATIONAL',
];

const ApplicantAnalysis = () => {
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams({
    stages: ['SUBSCRIBED'],
    status: 'ACTIVE',
    sort: 'match',
    direction: 'descend',
  });
  const { id } = useTypedParams({ id: JobPostingId });
  useEffect(() => {
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const queryClient = useQueryClient();

  const stagesPages = useInfiniteAPIQuery([
    '/stages/',
    {
      params: {
        job_posting_id: id,
      },
    },
  ]);

  const stages = usePrefetchPages(stagesPages, (data) => data.results);

  const currentStage = stages.find(
    ({ id, type }) =>
      searchParams.get('stages') === type || searchParams.get('stages') === id.toString()
  );

  const isInterviewStage = [
    'SCREENING_INTERVIEW',
    'HR_INTERVIEW',
    'MANAGER_INTERVIEW',
    'STAKEHOLDERS_INTERVIEW',
    'PRACTICAL_TECHNICAL_CASE',
  ].includes(currentStage?.sub_type || '');

  const jobPosting = useAPIQuery(`/job-postings/${id}/`);

  useEffect(() => {
    let canceled = false;

    const selectColumns = async () => {
      if (searchParams.has('columns')) return;

      const jobPosting = await queryClient.fetchQuery<JobPostingDetail>(`/job-postings/${id}/`);

      if (jobPosting.function_id) {
        let mindsightFunctions: MindsightFunction[] = [];
        let mindsightFunctionsPage: PaginatedResponse<MindsightFunction> = {
          next: undefined,
          count: 0,
          results: [],
        };

        do {
          mindsightFunctionsPage = await queryClient.fetchQuery(
            mindsightFunctionsPage.next ?? '/mindsight/ats-integrations/functions/'
          );

          mindsightFunctions = mindsightFunctions.concat(mindsightFunctionsPage.results);
        } while (mindsightFunctionsPage.next);

        searchParams.delete('columns');
        searchParams.append('columns', 'TAGS');
        searchParams.append('columns', 'MATCH');
        searchParams.append('columns', 'OCP');

        const keys: string[] = [];

        mindsightFunctions
          .find((fn) => fn.id === jobPosting.function_id)
          ?.required_assessments.forEach(({ key }) => {
            searchParams.append('columns', key.toUpperCase());
            keys.push(key.toUpperCase());
          });

        if (keys.includes('LOGIC') && keys.includes('SJT') && keys.includes('MOTIVATIONAL')) {
          searchParams.append('columns', 'RAW_POTENTIAL');
        }
      } else {
        for (const column of gradeColumnOptions) {
          searchParams.append('columns', column);
        }
      }

      if (isInterviewStage) {
        searchParams.append('columns', 'OPINION_STATUS');
      }

      if (canceled) return;

      setSearchParams(searchParams, { replace: true });
    };

    selectColumns();

    return () => {
      canceled = true;
    };
  }, [id, isInterviewStage, queryClient, searchParams, setSearchParams]);

  const { tenant } = useTypedParams({ tenant: String });

  const navigate = useTypedNavigate();

  const [changeApplicantsStage, setChangeApplicantStage] = useState<CandidateCollection['id'][]>(
    []
  );
  const [bulkMoveCandidates, setBulkMoveCandidates] = useState<boolean>(false);
  const [sendEmailApplicants, setSendEmailApplicants] = useState<CandidateCollection['id'][]>([]);
  const [bulkSendCommunication, setBulkSendCommunication] = useState<boolean>(false);
  const [sendAdditionalQuestionsApplicants, setSendAdditionalQuestionsApplicants] = useState<
    CandidateCollection['id'][]
  >([]);
  const [bulkSendAdditionalQuestions, setBulkSendAdditionalQuestions] = useState<boolean>(false);
  const [sendTestsApplicants, setSendTestsApplicants] = useState<CandidateCollection['id'][]>([]);
  const [bulkSendTests, setBulkSendTests] = useState<boolean>(false);
  const [sendCustomTestsApplicants, setSendCustomTestsApplicants] = useState<
    CandidateCollection['id'][]
  >([]);
  const [bulkSendCustomTests, setBulkSendCustomTests] = useState<boolean>(false);
  const [sendIntegrationAdmission, setSendIntegrationAdmission] =
    useState<CandidateCollection['id']>();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [rollbackApplicant, setRollbackApplicant] = useState<CandidateCollection['id']>();
  const [bulkRejectCandidates, setBulkRejectCandidates] = useState<boolean>(false);
  const [rejectApplicant, setRejectApplicant] = useState<CandidateCollection['id'][]>([]);
  const [applicantAnnotation, setApplicantAnnotation] = useState<CandidateCollection['id']>();
  const [applicantSelection, setApplicantSelection] = useState<CandidateCollection['id'][]>([]);
  const [sendPracticalTechnicalCaseCandidates, setSendPracticalTechnicalCaseCandidates] = useState<
    CandidateCollection['id'][]
  >([]);
  const [bulkSelect, setBulkSelect] = useState<boolean>(false);
  const [giveUpApplicant, setGiveUpApplicant] = useState<SearchCandidate>();
  const [isAddCandidateOpen, setIsAddCandidateOpen] = useState(false);
  const [bulkSendPracticalTechnicalCase, setBulkSendPracticalTechnicalCase] =
    useState<boolean>(false);

  const config = useAPIQuery('/configs/tenant-configurations/');

  const recruitmentProcessesPages = useInfiniteAPIQuery([
    '/recruitment-processes/',
    {
      params: {
        job_posting: id,
        has_answered_questions: true,
      },
    },
  ]);
  const recruitmentProcesses = usePrefetchPages(recruitmentProcessesPages, (page) => page.results);

  const candidateSourceOptionsPages = useInfiniteAPIQuery(['/candidate-source-options/']);

  const candidateSourceOptions = usePrefetchPages(
    candidateSourceOptionsPages,
    (page) => page.results
  );

  const allCandidatesWhoAnsweredQuestions = recruitmentProcesses.map(
    (recruitmentProcess) => recruitmentProcess.candidate.id
  );

  const searchCandidates = useSearchCandidatesController({
    functionId: jobPosting.data?.function_id || undefined,
    jobPostingId: id,
    enabled: !stagesPages.isFetching && !jobPosting.isFetching,
  });

  const tableCandidateDataSource = searchCandidates.data;

  const questionnaire = useAPIQuery([
    `/questions/`,
    {
      params: {
        job_posting_id: id,
      },
    },
  ]);

  const isHiredStage = searchParams.get('stages') === 'HIRED';

  const isUsingAdmissionIntegration =
    !!config.data?.enableSankhyaAdmission || !!config.data?.enableVixtingAdmission;

  const showIntegrationAdmissionLogs = !!config.data?.enableVixtingAdmission;

  const showIntegrationAdmissionFeatures = isUsingAdmissionIntegration && isHiredStage;

  const integration: 'sankhya' | 'vixting' = config.data?.enableVixtingAdmission
    ? 'vixting'
    : 'sankhya';

  const admissionIntegration = useAPIQueries(
    (searchCandidates.data?.results ?? []).map((candidate) => [
      `/${integration}/${integration}-admissions/`,
      {
        params: {
          recruitment_process: candidate.recruitmentProcess?.id,
        },
      },
    ]),
    {
      select(data) {
        return data.flatMap((data) => data.results[0]);
      },
      enabled: showIntegrationAdmissionFeatures,
    }
  );

  const markAsUnviewedMutation = useAPIMutation(
    async ({ id }: { id: number | null }, api) => {
      await api.delete(`/recruitment-process-user-visualizations/${id}/`);
    },
    {
      onSuccess: (_, __) => {
        queryClient.invalidateQueries('useSearchCandidates');
      },
    }
  );

  const candidateTagsPages = useInfiniteAPIQuery('/candidate-tags/');
  const candidateTags = usePrefetchPages(candidateTagsPages, (data) => data.results);

  const [addressState, setAddressState] = useState('');
  const [addressCity, setAddressCity] = useState('');
  const applicantCurrentStageName = useMemo(() => {
    const stageId = tableCandidateDataSource?.results.find(
      (candidate) => candidate.id === applicantAnnotation
    )?.stage?.id;
    return stages.find((stage) => stage.id === stageId)?.name || '-';
  }, [applicantAnnotation, stages, tableCandidateDataSource?.results]);

  const { canManage } = useCanAccessJobPosting({ jobPostingId: id });

  const createInterview = useCreateInterviewMutation({
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries('/interviews/');
      navigate({
        pathname: '/:tenant/opportunities/:id/interview/:interview_id/',
        params: {
          tenant,
          id,
          interview_id: data.id,
        },
      });
    },
  });

  const gradeColumnOptionsWithOpinion = [
    ...gradeColumnOptions,
    ...(isInterviewStage ? ['OPINION_STATUS'] : []),
  ];

  const interviews = useAPIQuery(
    [
      '/interviews/',
      {
        params: {
          recruitment_process__in: searchCandidates.data?.results
            .map(
              ({ recruitmentProcess }) =>
                recruitmentProcess?.id as RecruitmentProcessCollection['id']
            )
            .join(','),
          stage: parseInt(searchParams.get('stages') || '', 10),
        },
      },
    ],
    {
      enabled: Boolean(
        searchCandidates.isSuccess &&
          searchCandidates.data.results.length > 0 &&
          !isNaN(parseInt(searchParams.get('stages') || '', 10)) &&
          currentStage?.type === 'CUSTOM' &&
          isInterviewStage
      ),
    }
  );

  const isTableLoading =
    searchCandidates.isIdle || searchCandidates.isFetching || candidateTagsPages.isLoading;

  return (
    <>
      <PartialMoveCandidateStageModal
        open={changeApplicantsStage.length > 0}
        onCancel={() => setChangeApplicantStage([])}
        jobPostingId={id}
        candidatesIds={changeApplicantsStage}
        onSuccess={() => {
          setSelectedRowKeys([]);
          setChangeApplicantStage([]);
          searchParams.set('page', '1');
          setSearchParams(searchParams);
        }}
        destroyOnClose
      />
      <BulkMoveCandidateStageModal
        open={bulkMoveCandidates}
        onCancel={() => setBulkMoveCandidates(false)}
        jobPostingId={id}
        functionId={jobPosting.data?.function_id}
        onSuccess={() => {
          setBulkMoveCandidates(false);
          searchParams.set('page', '1');
          setSearchParams(searchParams);
        }}
        totalCount={tableCandidateDataSource?.count}
        destroyOnClose
      />
      {applicantAnnotation && (
        <ApplicantAnnotationModal
          open={!!applicantAnnotation}
          onCancel={() => setApplicantAnnotation(undefined)}
          candidateId={applicantAnnotation}
          fromStage={applicantCurrentStageName}
          jobPostingId={jobPosting.data?.id}
          onSuccess={() => {
            message.success(t('Comentário registrado.'));
            setApplicantAnnotation(undefined);
          }}
          destroyOnClose
        />
      )}
      <BulkSelectCandidateModal
        open={bulkSelect}
        actionOrigin="Triagem entre vagas"
        totalCount={tableCandidateDataSource?.count}
        onCancel={() => setBulkSelect(false)}
        onSuccess={() => {
          message.success(t('Candidato(s) triado(s).'));
          setBulkSelect(false);
        }}
        functionId={jobPosting.data?.function_id || undefined}
        jobPostingId={id}
        destroyOnClose
      />
      <PartialSelectCandidateModal
        ids={applicantSelection}
        actionOrigin="Triagem entre vagas"
        open={applicantSelection.length > 0}
        onCancel={() => setApplicantSelection([])}
        onSuccess={() => {
          message.success(t('Candidato(s) triado(s).'));
          setSelectedRowKeys([]);
          setApplicantSelection([]);
        }}
        destroyOnClose
      />
      <BulkSendEmailModal
        open={bulkSendCommunication}
        totalCount={tableCandidateDataSource?.count}
        onCancel={() => setBulkSendCommunication(false)}
        onSuccess={() => {
          message.success(t('Mensagem enviada.'));
          setBulkSendCommunication(false);
        }}
        functionId={jobPosting.data?.function_id || undefined}
        jobPostingId={id}
        destroyOnClose
      />
      <PartialSendEmailModal
        jobPostingId={id}
        candidateIds={sendEmailApplicants}
        open={sendEmailApplicants.length > 0}
        onCancel={() => setSendEmailApplicants([])}
        onSuccess={() => {
          message.success(t('Mensagem enviada.'));
          setSelectedRowKeys([]);
          setSendEmailApplicants([]);
        }}
        destroyOnClose
      />
      <BulkSendTestsModal
        jobPostingId={id}
        open={bulkSendTests}
        width={800}
        totalCount={tableCandidateDataSource?.count}
        onSuccess={() => {
          message.success(t('Testes enviados.'));
          setBulkSendTests(false);
        }}
        functionId={jobPosting.data?.function_id}
        onCancel={() => setBulkSendTests(false)}
        destroyOnClose
      />
      <PartialSendTestsModal
        jobPostingId={id}
        candidateIds={sendTestsApplicants}
        open={sendTestsApplicants.length > 0}
        width={800}
        onSuccess={() => {
          message.success(t('Testes enviados.'));
          setSelectedRowKeys([]);
          setSendTestsApplicants([]);
        }}
        onCancel={() => setSendTestsApplicants([])}
        destroyOnClose
      />
      <BulkSendAdditionalQuestionsModal
        jobPostingId={id}
        functionId={jobPosting.data?.function_id}
        totalCount={tableCandidateDataSource?.count}
        open={bulkSendAdditionalQuestions}
        onCancel={() => setBulkSendAdditionalQuestions(false)}
        onSuccess={() => {
          message.success(t('Perguntas de pré-requisito enviadas.'));
          setBulkSendAdditionalQuestions(false);
        }}
        destroyOnClose
      />
      <PartialSendAdditionalQuestionsModal
        jobPostingId={id}
        candidateIds={sendAdditionalQuestionsApplicants}
        open={sendAdditionalQuestionsApplicants.length > 0}
        onCancel={() => setSendAdditionalQuestionsApplicants([])}
        onSuccess={() => {
          message.success(t('Perguntas de pré-requisito enviadas.'));
          setSelectedRowKeys([]);
          setSendAdditionalQuestionsApplicants([]);
        }}
        destroyOnClose
      />
      <PartialSendPracticalTechnicalCaseModal
        jobPostingId={id}
        candidateIds={sendPracticalTechnicalCaseCandidates}
        stage={currentStage}
        open={sendPracticalTechnicalCaseCandidates.length > 0}
        width={800}
        onCancel={() => setSendPracticalTechnicalCaseCandidates([])}
        onSuccess={() => {
          message.success(t('Cases técnicos ou práticos enviados.'));
          setSelectedRowKeys([]);
          setSendPracticalTechnicalCaseCandidates([]);
        }}
        destroyOnClose
      />
      <BulkSendPracticalTechnicalCase
        jobPostingId={id}
        functionId={jobPosting.data?.function_id || undefined}
        totalCount={tableCandidateDataSource?.count}
        width={800}
        stage={currentStage}
        open={bulkSendPracticalTechnicalCase}
        onCancel={() => setBulkSendPracticalTechnicalCase(false)}
        onSuccess={() => {
          message.success(t('Cases técnicos ou práticos enviados.'));
          setBulkSendPracticalTechnicalCase(false);
        }}
        destroyOnClose
      />
      {config.data?.enableAlchemerIntegration && (
        <>
          <BulkSendCustomTestsModal
            jobPostingId={id}
            functionId={jobPosting.data?.function_id}
            open={bulkSendCustomTests}
            width={800}
            totalCount={tableCandidateDataSource?.count}
            onSuccess={() => {
              message.success(t('Testes customizados enviados.'), 6);
              setBulkSendCustomTests(false);
            }}
            onCancel={() => setBulkSendCustomTests(false)}
            destroyOnClose
          />
          <PartialSendCustomTestsModal
            jobPostingId={id}
            candidateIds={sendCustomTestsApplicants}
            open={sendCustomTestsApplicants.length > 0}
            width={800}
            onSuccess={() => {
              message.success(t('Testes customizados enviados.'), 6);
              setSelectedRowKeys([]);
              setSendCustomTestsApplicants([]);
            }}
            onCancel={() => setSendCustomTestsApplicants([])}
            destroyOnClose
          />
        </>
      )}
      <PartialRejectCandidateModal
        okButtonProps={{ htmlType: 'submit', icon: <MinusSquareFilled />, danger: true }}
        okText="Reprovar"
        candidateIds={rejectApplicant}
        jobPostingId={id}
        open={rejectApplicant.length > 0}
        onCancel={() => setRejectApplicant([])}
        onSuccess={() => {
          message.success(t('Candidato(s) reprovado(s).'));
          setSelectedRowKeys([]);
          setRejectApplicant([]);
          searchParams.set('page', '1');
          setSearchParams(searchParams);
        }}
        destroyOnClose
      />
      <BulkRejectCandidateModal
        jobPostingId={id}
        functionId={jobPosting.data?.function_id}
        okButtonProps={{ htmlType: 'submit', icon: <MinusSquareFilled />, danger: true }}
        okText="Reprovar"
        open={bulkRejectCandidates}
        totalCount={tableCandidateDataSource?.count}
        onCancel={() => setBulkRejectCandidates(false)}
        onSuccess={() => {
          message.success(t('Candidato(s) reprovado(s).'));
          setBulkRejectCandidates(false);
          searchParams.set('page', '1');
          setSearchParams(searchParams);
        }}
        destroyOnClose
      />
      {rollbackApplicant && (
        <ActivateApplicantModal
          okText="Retornar"
          okButtonProps={{ htmlType: 'submit', icon: <RollbackOutlined /> }}
          candidateId={rollbackApplicant}
          jobPostingId={id}
          open={!!rollbackApplicant}
          onCancel={() => setRollbackApplicant(undefined)}
          onSuccess={() => {
            message.success(t('Candidato retornado.'));
            setRollbackApplicant(undefined);
          }}
          destroyOnClose
        />
      )}
      {giveUpApplicant && (
        <GiveUpApplicantModal
          open={!!giveUpApplicant}
          onCancel={() => setGiveUpApplicant(undefined)}
          candidate={giveUpApplicant}
          jobPostingId={id}
          onSuccess={() => {
            message.success(t('Candidato marcado como desistente.'));
            setGiveUpApplicant(undefined);
          }}
          destroyOnClose
        />
      )}
      {config.data?.enableSankhyaAdmission && (
        <SendSankhyaAdmissionModal
          open={!!sendIntegrationAdmission}
          onCancel={() => setSendIntegrationAdmission(undefined)}
          jobPostingId={id}
          candidateId={sendIntegrationAdmission}
          onSuccess={() => setSendIntegrationAdmission(undefined)}
          destroyOnClose
        />
      )}
      {config.data?.enableVixtingAdmission && (
        <SendVixtingAdmissionModal
          open={!!sendIntegrationAdmission}
          onCancel={() => setSendIntegrationAdmission(undefined)}
          jobPostingId={id}
          candidateId={sendIntegrationAdmission}
          onSuccess={() => setSendIntegrationAdmission(undefined)}
          destroyOnClose
        />
      )}
      {isAddCandidateOpen && (
        <AddCandidateDrawer
          open={isAddCandidateOpen}
          jobPostingId={id}
          onSuccess={(candidateId) => {
            message.success(t('Candidato adicionado no banco de talentos.'));
            setIsAddCandidateOpen(false);
            navigate({
              pathname: '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/details/',
              params: { tenant, id, applicant_id: candidateId },
              search: searchParams,
            });
          }}
          onClose={() => setIsAddCandidateOpen(false)}
        />
      )}
      <ErrorResult
        error={stagesPages.error || searchCandidates.error || jobPosting.error || interviews.error}
      >
        <Helmet>
          <title>
            {t('Analisar Candidatos')} | {jobPosting.data?.name || '...'} |{' '}
            {t('Recrutamento e Seleção')}
          </title>
        </Helmet>
        <PageContent>
          <Outlet />
          <Card data-testid="main-card">
            <Space direction="vertical" size="middle">
              <Filter
                onClear={() => {
                  searchParams.set('page', '1');
                  searchParams.set('status', 'ACTIVE');
                  searchParams.delete('search');
                  searchParams.delete('attachment_search');
                  deleteScoreSearchParams(searchParams);
                  searchParams.delete('disabilities');
                  searchParams.delete('addressContryIn');
                  searchParams.delete('addressStateIn');
                  searchParams.delete('addressCityIn');
                  searchParams.delete('updatedAtMin');
                  searchParams.delete('updatedAtMax');
                  searchParams.delete('createdAtMin');
                  searchParams.delete('createdAtMax');
                  Array.from(searchParams.entries())
                    .filter(([key]) => /question\[\w+\]/.test(key))
                    .forEach(([key]) => searchParams.delete(key));
                  searchParams.delete('age_start');
                  searchParams.delete('age_end');
                  searchParams.delete('salary_expectation_start');
                  searchParams.delete('salary_expectation_end');
                  searchParams.delete('candidateSourceOptions');
                  searchParams.delete('testCompleteness');
                  searchParams.delete('additionalQuestionsCompleteness');
                  searchParams.delete('tags');
                  setSearchParams(searchParams);
                }}
              >
                <Col span={20}>
                  <OpportunityTabs />
                </Col>
                <Col span={4}>
                  {canManage && (
                    <Button
                      type="default"
                      htmlType="submit"
                      icon={<UserAddOutlined />}
                      onClick={() => setIsAddCandidateOpen(true)}
                    >
                      {' '}
                      <Trans>Adicionar candidato</Trans>
                    </Button>
                  )}
                </Col>
                <Col span={24}>
                  <SearchParam
                    name="stages"
                    defaultValue=""
                    valuePropName="activeKey"
                    onSetSearchParams={(searchParams) => {
                      const stageId = parseInt(searchParams.get('stages') || '', 10);

                      if (stageId) {
                        const stage = stages.find((stage) => stageId === stage.id);
                        if (
                          [
                            'SCREENING_INTERVIEW',
                            'HR_INTERVIEW',
                            'MANAGER_INTERVIEW',
                            'STAKEHOLDERS_INTERVIEW',
                            'PRACTICAL_TECHNICAL_CASE',
                          ].includes(stage?.sub_type || '') &&
                          !searchParams.getAll('columns').includes('OPINION_STATUS')
                        ) {
                          searchParams.append('columns', 'OPINION_STATUS');
                        }
                      }
                      searchParams.set('page', '1');
                    }}
                  >
                    <Tabs
                      type="card"
                      onTabClick={(activeKey, _) => {
                        const stage = stages.find(
                          (stage) => activeKey === stage.id.toString() || activeKey === stage.type
                        );
                        queryClient.invalidateQueries(['/interviews/']);
                        queryClient.invalidateQueries([
                          '/recruitment-processes/',
                          {
                            params: {
                              stage_id: stage?.id,
                              status:
                                (searchParams.get('status') as
                                  | RecruitmentProcessCollection['status']
                                  | null) || undefined,
                              job_posting: !stage ? id : undefined,
                            },
                          },
                        ]);
                      }}
                      items={[
                        ...stages.map((stage) => ({
                          key: stage.type !== 'CUSTOM' ? stage.type : stage.id.toString(),
                          label: (
                            <StageAggregateTitle
                              id={stage.id}
                              name={stage.name}
                              type={stage.type}
                              status={
                                (searchParams.get('status') as
                                  | RecruitmentProcessCollection['status']
                                  | null) || undefined
                              }
                              jobPostingId={id}
                            />
                          ),
                        })),
                        {
                          key: '',
                          label: (
                            <StageAggregateTitle
                              name={t('Todas')}
                              status={
                                (searchParams.get('status') as
                                  | RecruitmentProcessCollection['status']
                                  | null) || undefined
                              }
                              jobPostingId={id}
                            />
                          ),
                        },
                      ]}
                    />
                  </SearchParam>
                </Col>
                <Col flex={1}>
                  <SearchParam name="search" onSetSearchParams={{ page: '1' }}>
                    <Input.Search
                      data-testid="search-input"
                      allowClear
                      placeholder={t('Buscar candidatos')}
                    />
                  </SearchParam>
                </Col>
                {config.data?.enableFileProcessingWithSearchEngine && (
                  <Col style={{ width: 450 }}>
                    <SearchAttachmentContent
                      isTableLoading={isTableLoading}
                      searchParams={searchParams}
                      searchCandidateCount={tableCandidateDataSource?.count}
                    />
                  </Col>
                )}
                <SearchParam name="status" defaultValue="" onSetSearchParams={{ page: '1' }}>
                  <Select allowClear style={{ width: 120 }} data-testid="select-candidate-status">
                    <Select.Option value="" data-testid="select-stage-all">
                      <Trans>Todos</Trans>
                    </Select.Option>
                    <Select.Option value="ACTIVE" data-testid="select-stage-active">
                      <Trans>Ativos</Trans>
                    </Select.Option>
                    <Select.Option value="INACTIVE" data-testid="select-stage-inactive">
                      <Trans>Reprovados</Trans>
                    </Select.Option>
                    <Select.Option value="GIVEN_UP" data-testid="select-stage-givenup">
                      <Trans>Desistentes</Trans>
                    </Select.Option>
                  </Select>
                </SearchParam>
                <Filter.Item
                  name="testCompleteness"
                  label={t('Status dos testes')}
                  selected={
                    <Filter.Selection name="testCompleteness" onSetSearchParams={{ page: '1' }}>
                      <Filter.Tag defaultValue="MISSING">
                        <Trans>Candidatos com testes não finalizados</Trans>
                      </Filter.Tag>
                      <Filter.Tag defaultValue="FINALIZED">
                        <Trans>Candidatos com testes finalizados</Trans>
                      </Filter.Tag>
                    </Filter.Selection>
                  }
                  // TODO: VER SE TRADUZ AQUI
                  group="Testes"
                >
                  <Space direction="vertical">
                    <SearchParam name="testCompleteness" onSetSearchParams={{ page: '1' }}>
                      <Radio.Group>
                        <Space direction="vertical">
                          <Radio value="MISSING">
                            <Trans>Testes não finalizados</Trans>
                          </Radio>
                          <Radio value="FINALIZED">
                            <Trans>Testes finalizados</Trans>
                          </Radio>
                        </Space>
                      </Radio.Group>
                    </SearchParam>
                    <SearchParam
                      array
                      name="testCompleteness"
                      onSetSearchParams={{ page: '1' }}
                      valuePropName="disabledValue"
                    >
                      <Filter.Button>
                        <Trans>Limpar</Trans>
                      </Filter.Button>
                    </SearchParam>
                  </Space>
                </Filter.Item>
                <Filter.Item
                  label={t('Match')}
                  name="match"
                  selected={
                    <RangeFilterTag
                      nameMin="match_min"
                      nameMax="match_max"
                      label={t('Match')}
                      onSetSearchParams={{ page: '1' }}
                    />
                  }
                  group="Testes"
                >
                  <Filter.Range nameMin="match_min" nameMax="match_max" />
                </Filter.Item>
                <Filter.Item
                  label={t('Potencial Bruto')}
                  name="raw_potential"
                  selected={
                    <RangeFilterTag
                      nameMin="raw_potential_min"
                      nameMax="raw_potential_max"
                      label={t('Potencial Bruto')}
                      onSetSearchParams={{ page: '1' }}
                    />
                  }
                  group="Testes"
                >
                  <Filter.Range nameMin="raw_potential_min" nameMax="raw_potential_max" />
                </Filter.Item>
                <Filter.Item
                  label={t('Fit Cultural')}
                  name="ocp"
                  selected={
                    <RangeFilterTag
                      nameMin="ocp_min"
                      nameMax="ocp_max"
                      label={t('Fit Cultural')}
                      onSetSearchParams={{ page: '1' }}
                    />
                  }
                  group="Testes"
                >
                  <Filter.Range nameMin="ocp_min" nameMax="ocp_max" />
                </Filter.Item>
                <Filter.Item
                  label={t('Mental')}
                  name="logic"
                  selected={
                    <RangeFilterTag
                      nameMin="logic_min"
                      nameMax="logic_max"
                      label={t('Raciocínio')}
                      onSetSearchParams={{ page: '1' }}
                    />
                  }
                  group="Testes"
                >
                  <Filter.Range nameMin="logic_min" nameMax="logic_max" />
                </Filter.Item>
                <Filter.Item
                  label={t('Social')}
                  name="sjt"
                  selected={
                    <RangeFilterTag
                      nameMin="sjt_min"
                      nameMax="sjt_max"
                      label={t('Social')}
                      onSetSearchParams={{ page: '1' }}
                    />
                  }
                  group="Testes"
                >
                  <Filter.Range nameMin="sjt_min" nameMax="sjt_max" />
                </Filter.Item>
                <Filter.Item
                  label={t('Motivacional')}
                  name="motivational"
                  selected={
                    <RangeFilterTag
                      nameMin="motivational_min"
                      nameMax="motivational_max"
                      label={t('Motivacional')}
                      onSetSearchParams={{ page: '1' }}
                    />
                  }
                  group="Testes"
                >
                  <Filter.Range nameMin="motivational_min" nameMax="motivational_max" />
                </Filter.Item>
                <Filter.Item
                  label={t('Raciocínio Simplificado')}
                  name="operational_logic"
                  selected={
                    <Filter.Selection name="operational_logic" onSetSearchParams={{ page: '1' }}>
                      <Filter.Tag defaultValue="Aprovado">
                        <Trans>Simplificado de raciocínio: Aprovado</Trans>
                      </Filter.Tag>
                      <Filter.Tag defaultValue="Reprovado">
                        <Trans>Simplificado de raciocínio: Reprovado</Trans>
                      </Filter.Tag>
                    </Filter.Selection>
                  }
                  group="Testes"
                >
                  <Filter.RadioGroup name="operational_logic">
                    <Radio value="Aprovado">
                      <Trans>Aprovado</Trans>
                    </Radio>
                    <Radio value="Reprovado">
                      <Trans>Reprovado</Trans>
                    </Radio>
                  </Filter.RadioGroup>
                </Filter.Item>
                <Filter.Item
                  name="disabilities"
                  label={t('Pessoas com Deficiência')}
                  selected={
                    <Filter.Selection name="disabilities" onSetSearchParams={{ page: '1' }}>
                      <Filter.Tag defaultValue="HEARING">
                        <Trans>Auditiva</Trans>
                      </Filter.Tag>
                      <Filter.Tag defaultValue="PHYSICAL">
                        <Trans>Física</Trans>
                      </Filter.Tag>
                      <Filter.Tag defaultValue="MENTAL">
                        <Trans>Mental</Trans>
                      </Filter.Tag>
                      <Filter.Tag defaultValue="VISUAL">
                        <Trans>Visual</Trans>
                      </Filter.Tag>
                      <Filter.Tag defaultValue="INTELLECTUAL">
                        <Trans>Intelectual</Trans>
                      </Filter.Tag>
                      <Filter.Tag defaultValue="OTHER">
                        <Trans>Outra</Trans>
                      </Filter.Tag>
                    </Filter.Selection>
                  }
                >
                  <SearchParam name="disabilities" array onSetSearchParams={{ page: '1' }}>
                    <Select
                      data-testid="filter-candidate-disabilities-input"
                      mode="multiple"
                      style={{ width: '100%' }}
                      maxTagCount="responsive"
                    >
                      <Select.Option value="HEARING">
                        <Trans>Auditiva</Trans>
                      </Select.Option>
                      <Select.Option value="PHYSICAL">
                        <Trans>Física</Trans>
                      </Select.Option>
                      <Select.Option value="MENTAL">
                        <Trans>Mental</Trans>
                      </Select.Option>
                      <Select.Option value="VISUAL">
                        <Trans>Visual</Trans>
                      </Select.Option>
                      <Select.Option value="INTELLECTUAL">
                        <Trans>Intelectual</Trans>
                      </Select.Option>
                      <Select.Option value="OTHER">
                        <Trans>Outra</Trans>
                      </Select.Option>
                    </Select>
                  </SearchParam>
                </Filter.Item>
                <Filter.Section label={t('Localidade')}>
                  {[
                    <Filter.Item
                      name="addressContryIn"
                      key="country"
                      label={t('País')}
                      hideSelected
                      selected={
                        <Filter.Selection name="addressContryIn" onSetSearchParams={{ page: '1' }}>
                          {(searchParams.getAll('addressContryIn') as string[]).map(
                            (countryName) => (
                              <Filter.Tag key={countryName} defaultValue={countryName}>
                                <Trans>País</Trans>:{' '}
                                {
                                  countries.find(
                                    (country) => country.alpha2.toLocaleUpperCase() === countryName
                                  )?.name
                                }
                              </Filter.Tag>
                            )
                          )}
                        </Filter.Selection>
                      }
                    >
                      <SearchParam name="addressContryIn" array onSetSearchParams={{ page: '1' }}>
                        <Select
                          allowClear
                          mode="multiple"
                          showSearch
                          filterOption={(input, option) =>
                            (option?.children as unknown as string)
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          style={{ width: '100%' }}
                          data-testid="country-candidate-filter-select"
                        >
                          {countries.map((country) => (
                            <Select.Option
                              key={country.id}
                              value={country.alpha2.toLocaleUpperCase()}
                            >
                              {country.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </SearchParam>
                    </Filter.Item>,
                    <Filter.Item
                      name="addressStateIn"
                      key="state"
                      label={t('Estado')}
                      selected={
                        <Filter.Selection name="addressStateIn" onSetSearchParams={{ page: '1' }}>
                          {(searchParams.getAll('addressStateIn') as string[]).map((state) => (
                            <Filter.Tag key={state} defaultValue={state}>
                              <Trans>Estado</Trans>: {state}
                            </Filter.Tag>
                          ))}
                        </Filter.Selection>
                      }
                    >
                      <SearchParam name="addressStateIn" array onSetSearchParams={{ page: '1' }}>
                        {({ setSearchParam, value }) => (
                          <Input
                            data-testid="state-candidate-filter-input"
                            value={addressState}
                            onChange={(e) => setAddressState(e.target.value)}
                            onPressEnter={() => {
                              if (Array.isArray(value) && !value.includes(addressState)) {
                                setSearchParam([...value, addressState]);
                              }
                              setAddressState('');
                            }}
                          />
                        )}
                      </SearchParam>
                    </Filter.Item>,
                    <Filter.Item
                      name="addressCityIn"
                      label={t('Cidade')}
                      key="city"
                      selected={
                        <Filter.Selection name="addressCityIn" onSetSearchParams={{ page: '1' }}>
                          {(searchParams.getAll('addressCityIn') as string[]).map((city) => (
                            <Filter.Tag key={city} defaultValue={city}>
                              <Trans>Cidade</Trans>: {city}
                            </Filter.Tag>
                          ))}
                        </Filter.Selection>
                      }
                    >
                      <SearchParam name="addressCityIn" array onSetSearchParams={{ page: '1' }}>
                        {({ setSearchParam, value }) => (
                          <Input
                            data-testid="city-candidate-filter-input"
                            value={addressCity}
                            onChange={(e) => setAddressCity(e.target.value)}
                            onPressEnter={() => {
                              if (Array.isArray(value) && !(addressCity in value)) {
                                setSearchParam([...value, addressCity]);
                              }
                              setAddressCity('');
                            }}
                          />
                        )}
                      </SearchParam>
                    </Filter.Item>,
                  ]}
                </Filter.Section>
                <Filter.Item
                  name="updated_at"
                  label={t('Data de atualização do currículo')}
                  selected={
                    <RangeFilterTag
                      nameMin="updatedAtMin"
                      nameMax="updatedAtMax"
                      label={t('Data de atualização do currículo')}
                      onSetSearchParams={{ page: '1' }}
                    />
                  }
                >
                  <Filter.DateRange start="updatedAtMin" end="updatedAtMax" />
                </Filter.Item>
                <Filter.Item
                  name="created_at"
                  label={t('Data de inscrição na vaga')}
                  selected={
                    <RangeFilterTag
                      nameMin="createdAtMin"
                      nameMax="createdAtMax"
                      label={t('Data de inscrição na vaga')}
                      onSetSearchParams={{ page: '1' }}
                    />
                  }
                >
                  <Filter.DateRange start="createdAtMin" end="createdAtMax" />
                </Filter.Item>
                {questionnaire.data?.count && questionnaire.data?.count > 0 ? (
                  <Filter.Section label={t('Perguntas de pré-requisito')}>
                    {[
                      <Filter.Item
                        key="additionalQuestionsCompleteness"
                        name="additionalQuestionsCompleteness"
                        label={t('Status das perguntas de pré-requisito')}
                        selected={
                          <Filter.Selection
                            name="additionalQuestionsCompleteness"
                            onSetSearchParams={{ page: '1' }}
                          >
                            <Filter.Tag defaultValue="MISSING">
                              <Trans>Perguntas de pré-requisito não respondidas</Trans>
                            </Filter.Tag>
                            <Filter.Tag defaultValue="FINALIZED">
                              <Trans>Perguntas de pré-requisito respondidas</Trans>
                            </Filter.Tag>
                          </Filter.Selection>
                        }
                      >
                        <Space direction="vertical">
                          <SearchParam
                            name="additionalQuestionsCompleteness"
                            onSetSearchParams={{ page: '1' }}
                          >
                            <Radio.Group>
                              <Space direction="vertical">
                                <Radio value="MISSING">
                                  <Trans>Perguntas de pré-requisito não respondidas</Trans>
                                </Radio>
                                <Radio value="FINALIZED">
                                  <Trans>Perguntas de pré-requisito respondidas</Trans>
                                </Radio>
                              </Space>
                            </Radio.Group>
                          </SearchParam>
                          <SearchParam
                            array
                            name="additionalQuestionsCompleteness"
                            onSetSearchParams={{ page: '1' }}
                            valuePropName="disabledValue"
                          >
                            <Filter.Button>
                              <Trans>Limpar</Trans>
                            </Filter.Button>
                          </SearchParam>
                        </Space>
                      </Filter.Item>,
                      ...(questionnaire.data?.results ?? []).map((question) => (
                        <Filter.Item
                          key={question.id}
                          name={`question[${question.id}]`}
                          label={question.description}
                          selected={
                            <Filter.Selection
                              key={question.id}
                              name={`question[${question.id}]`}
                              onSetSearchParams={{ page: '1' }}
                            >
                              {(searchParams.getAll(`question[${question.id}]`) as string[]).map(
                                (answerId) => (
                                  <Filter.Tag key={answerId} defaultValue={answerId}>
                                    <Typography.Text
                                      title={question.description}
                                      ellipsis
                                      style={{ maxWidth: 150 }}
                                    >
                                      {question.description}
                                    </Typography.Text>
                                    :{' '}
                                    <Typography.Text
                                      title={
                                        question.alternatives?.find(
                                          ({ id }) => id === Number(answerId)
                                        )?.description || answerId
                                      }
                                      ellipsis
                                      style={{ maxWidth: 150 }}
                                    >
                                      {question.alternatives?.find(
                                        ({ id }) => id === Number(answerId)
                                      )?.description || answerId}
                                    </Typography.Text>
                                  </Filter.Tag>
                                )
                              )}
                            </Filter.Selection>
                          }
                        >
                          {question.type === 'CHECKBOX' && (
                            <SearchParam name={`question[${question.id}]`} array valuePropName="id">
                              <Select
                                mode="multiple"
                                style={{ width: '100%' }}
                                maxTagCount="responsive"
                              >
                                {question.alternatives?.map((alternative) => {
                                  return (
                                    <Select.Option key={alternative.id} id={alternative.id}>
                                      {alternative.description}
                                    </Select.Option>
                                  );
                                })}
                              </Select>
                            </SearchParam>
                          )}
                          {question.type === 'RADIO' && (
                            <SearchParam name={`question[${question.id}]`} valuePropName="id">
                              <Select style={{ width: '100%' }} maxTagCount="responsive">
                                {question.alternatives?.map((alternative) => {
                                  return (
                                    <Select.Option key={alternative.id} id={alternative.id}>
                                      {alternative.description}
                                    </Select.Option>
                                  );
                                })}
                              </Select>
                            </SearchParam>
                          )}
                          {question.type === 'TEXT' && (
                            <SearchParam name={`question[${question.id}]`}>
                              <Input data-testid={`question-${question.id}-text-input`} />
                            </SearchParam>
                          )}
                        </Filter.Item>
                      )),
                    ]}
                  </Filter.Section>
                ) : null}
                <Col>
                  <Popover
                    placement="bottomRight"
                    content={
                      <Space direction="vertical">
                        <SearchParam name="columns">
                          <Checkbox.Group>
                            <Space direction="vertical">
                              {gradeColumnOptionsWithOpinion.map((column) => {
                                switch (column) {
                                  case 'TAGS':
                                    return (
                                      <Checkbox key={column} name="columns" value={column}>
                                        <Trans>Tags</Trans>
                                      </Checkbox>
                                    );
                                  case 'LOGIC':
                                    return (
                                      <Checkbox key={column} name="columns" value={column}>
                                        <Trans>Mental</Trans>
                                      </Checkbox>
                                    );
                                  case 'SJT':
                                    return (
                                      <Checkbox key={column} name="columns" value={column}>
                                        <Trans>Social</Trans>
                                      </Checkbox>
                                    );
                                  case 'MOTIVATIONAL':
                                    return (
                                      <Checkbox key={column} name="columns" value={column}>
                                        <Trans>Motivacional</Trans>
                                      </Checkbox>
                                    );
                                  case 'OPERATIONAL':
                                    return (
                                      <Checkbox key={column} name="columns" value={column}>
                                        <Trans>Raciocínio Simplificado</Trans>
                                      </Checkbox>
                                    );
                                  case 'MATCH':
                                    return (
                                      <Checkbox key={column} name="columns" value={column}>
                                        <Trans>Match</Trans>
                                      </Checkbox>
                                    );
                                  case 'RAW_POTENTIAL':
                                    return (
                                      <Checkbox key={column} name="columns" value={column}>
                                        <Trans>Potencial Bruto</Trans>
                                      </Checkbox>
                                    );
                                  case 'OCP':
                                    return (
                                      <Checkbox key={column} name="columns" value={column}>
                                        <Trans>Fit Cultural</Trans>
                                      </Checkbox>
                                    );
                                  case 'OPINION_STATUS':
                                    return (
                                      <Checkbox key={column} name="columns" value={column}>
                                        <Trans>Parecer</Trans>
                                      </Checkbox>
                                    );
                                }
                              })}
                            </Space>
                          </Checkbox.Group>
                        </SearchParam>
                        <Space>
                          <SearchParam array name="columns" valuePropName="disabledValue">
                            <Filter.Button value={gradeColumnOptionsWithOpinion}>
                              <Trans>Todas</Trans>
                            </Filter.Button>
                          </SearchParam>
                          <SearchParam array name="columns" valuePropName="disabledValue">
                            <Filter.Button>
                              <Trans>Limpar</Trans>
                            </Filter.Button>
                          </SearchParam>
                        </Space>
                      </Space>
                    }
                  >
                    <Button>
                      <TableOutlined />
                    </Button>
                  </Popover>
                </Col>
                <Filter.Item
                  name="birthday"
                  label={t('Idade')}
                  selected={
                    <RangeFilterTag
                      nameMin="age_start"
                      nameMax="age_end"
                      label={t('Idade')}
                      onSetSearchParams={{ page: '1' }}
                    />
                  }
                >
                  <Filter.Range nameMin="age_start" nameMax="age_end" />
                </Filter.Item>
                <Filter.Item
                  name="salaryExpectation"
                  label={t('Pretensão salarial')}
                  selected={
                    <SalaryRangeFilterTag
                      nameMin="salary_expectation_start"
                      nameMax="salary_expectation_end"
                      label={t('Pretensão salarial')}
                      onSetSearchParams={{ page: '1' }}
                    />
                  }
                >
                  <Filter.SalaryRange
                    nameMin="salary_expectation_start"
                    nameMax="salary_expectation_end"
                  />
                </Filter.Item>
                <Filter.Item
                  name="candidateSourceOptions"
                  label={t('Origem do Candidato')}
                  selected={
                    <Filter.Selection
                      name="candidateSourceOptions"
                      onSetSearchParams={{ page: '1' }}
                    >
                      {candidateSourceOptions.map((candidateSourceOption) => (
                        <Filter.Tag
                          key={candidateSourceOption.name}
                          defaultValue={candidateSourceOption.name}
                        >
                          {candidateSourceOption.name}
                        </Filter.Tag>
                      ))}
                    </Filter.Selection>
                  }
                >
                  <SearchParam
                    name="candidateSourceOptions"
                    array
                    onSetSearchParams={{ page: '1' }}
                  >
                    <Select
                      data-testid="filter-candidate-source-options-input"
                      mode="multiple"
                      style={{ width: '100%' }}
                      maxTagCount="responsive"
                      options={candidateSourceOptions.map((candidateSourceOption) => ({
                        value: candidateSourceOption.name,
                        label: candidateSourceOption.name,
                      }))}
                    />
                  </SearchParam>
                </Filter.Item>
                <Filter.Item
                  name="tags"
                  label={t('Tags')}
                  selected={
                    <Filter.Selection name="tags">
                      {candidateTags.map(({ id, name }) => (
                        <Filter.Tag key={id} defaultValue={`${id}`}>
                          {name}
                        </Filter.Tag>
                      ))}
                    </Filter.Selection>
                  }
                >
                  <SearchParam name="tags" array onSetSearchParams={{ page: '1' }}>
                    <Select
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      style={{ width: '100%' }}
                      mode="multiple"
                      maxTagCount="responsive"
                      options={candidateTags.map(({ id, name }) => ({
                        key: id.toString(),
                        value: id.toString(),
                        label: name,
                      }))}
                    />
                  </SearchParam>
                </Filter.Item>
              </Filter>
              {selectedRowKeys.length > 0 && (
                <ApplicantAnalysisBulkActions
                  candidateIds={selectedRowKeys.map(CandidateId)}
                  isHiredStage={isHiredStage}
                  jobPostingStatus={jobPosting.data?.status}
                  enableAlchemerIntegration={config.data?.enableAlchemerIntegration}
                  questionsCount={questionnaire.data?.count}
                  allCandidatesWhoAnsweredQuestions={allCandidatesWhoAnsweredQuestions}
                  sendPracticalTechnicalCase={currentStage?.sub_type === 'PRACTICAL_TECHNICAL_CASE'}
                  onMoveStageClick={() => setChangeApplicantStage(selectedRowKeys.map(CandidateId))}
                  onRejectClick={() => setRejectApplicant(selectedRowKeys.map(CandidateId))}
                  onSendEmailClick={() => setSendEmailApplicants(selectedRowKeys.map(CandidateId))}
                  onSendTestsClick={() => setSendTestsApplicants(selectedRowKeys.map(CandidateId))}
                  onSendCustomTestsClick={() =>
                    setSendCustomTestsApplicants(selectedRowKeys.map(CandidateId))
                  }
                  onSendAdditionalQuestionsClick={() =>
                    setSendAdditionalQuestionsApplicants(selectedRowKeys.map(CandidateId))
                  }
                  onSendPracticalTechnicalCase={() =>
                    setSendPracticalTechnicalCaseCandidates(selectedRowKeys.map(CandidateId))
                  }
                  onSelectClick={() => setApplicantSelection(selectedRowKeys.map(CandidateId))}
                  onClear={() => setSelectedRowKeys([])}
                />
              )}
              <CandidateTable
                dataSource={tableCandidateDataSource?.results.map((tableCandidate) => ({
                  opinion_status:
                    interviews.data?.results.find(
                      (interview) =>
                        interview.recruitment_process === tableCandidate.recruitmentProcess?.id
                    )?.opinion_status || 'PENDING',
                  ...tableCandidate,
                }))}
                opinionStatusColumnVisible={isInterviewStage}
                showIntegrationAdmissionStatus={showIntegrationAdmissionFeatures}
                showIntegrationAdmissionLogs={showIntegrationAdmissionLogs}
                integrationAdmissions={admissionIntegration.data}
                pagination={{
                  total: tableCandidateDataSource?.count,
                }}
                loading={isTableLoading || interviews.isLoading}
                rowSelection={
                  canManage
                    ? {
                        type: 'checkbox',
                        selectedRowKeys,
                        preserveSelectedRowKeys: true,
                        onChange: (selectedRowKeys) => setSelectedRowKeys(selectedRowKeys),
                        selections: [
                          {
                            key: 'bulk-move-candidates',
                            text: t('Mover todos os candidatos de etapa'),
                            onSelect: () => setBulkMoveCandidates(true),
                          },
                          ...(!isHiredStage
                            ? [
                                {
                                  key: 'bulk-reject-candidates',
                                  text: t('Reprovar todos os candidatos'),
                                  onSelect: () => {
                                    setBulkRejectCandidates(true);
                                  },
                                },
                              ]
                            : []),
                          {
                            key: 'bulk-send-communication',
                            text: t('Enviar comunicação para todos os candidatos'),
                            onSelect: () => {
                              setBulkSendCommunication(true);
                            },
                          },
                          {
                            key: 'bulk-send-tests',
                            text: t('Enviar testes para todos os candidatos'),
                            onSelect: () => {
                              setBulkSendTests(true);
                            },
                          },
                          ...(config.data?.enableAlchemerIntegration
                            ? [
                                {
                                  key: 'bulk-send-custom-tests',
                                  text: t('Enviar testes customizados para todos os candidatos'),
                                  onSelect: () => {
                                    setBulkSendCustomTests(true);
                                  },
                                },
                              ]
                            : []),
                          ...(questionnaire.data?.count
                            ? [
                                {
                                  key: 'bulk-send-additional-questions',
                                  text: t(
                                    'Enviar perguntas de pré-requisito para todos os candidatos'
                                  ),
                                  onSelect: () => {
                                    setBulkSendAdditionalQuestions(true);
                                  },
                                },
                              ]
                            : []),
                          ...(currentStage?.sub_type === 'PRACTICAL_TECHNICAL_CASE'
                            ? [
                                {
                                  key: 'bulk-send-practical-technical-case',
                                  text: t(
                                    'Enviar case técnico ou prático para todos os candidatos'
                                  ),
                                  onSelect: () => {
                                    setBulkSendPracticalTechnicalCase(true);
                                  },
                                },
                              ]
                            : []),
                          {
                            key: 'bulk-select',
                            text: t('Adicionar todos os candidatos à vaga'),
                            onSelect: () => {
                              setBulkSelect(true);
                            },
                          },
                        ],
                      }
                    : undefined
                }
                candidateTags={candidateTags}
              >
                <Table.Column
                  title={t('Etapa')}
                  dataIndex={['stage', 'id']}
                  className={styles.stageCell}
                  render={(stageId, record: SearchCandidate) => (
                    <Typography.Text data-testid={`candidate-stage-${record.id}`}>
                      {stages.find((stage) => stage.id === stageId)?.name}
                    </Typography.Text>
                  )}
                />
                <Table.Column
                  title={t('Ações')}
                  render={(_, record: SearchCandidate, index: number) => {
                    const integrationAdmissionStatus = admissionIntegration.data?.find(
                      (admission) =>
                        admission?.recruitment_process === record.recruitmentProcess?.id
                    )?.status;

                    const recruitmentProcessInterview = interviews.data?.results.find(
                      ({ recruitment_process }) =>
                        recruitment_process === record.recruitmentProcess?.id
                    );

                    return (
                      <ApplicantAnalysisTableActions
                        candidateId={record.id}
                        candidateName={record.name}
                        hasViewed={Boolean(record.recruiter_visualization_id)}
                        disableSendAdditionalQuestions={allCandidatesWhoAnsweredQuestions.includes(
                          record.id
                        )}
                        disableSendIntegrationAdmission={
                          integrationAdmissionStatus && integrationAdmissionStatus !== 'FAILED'
                        }
                        givenUp={
                          canManage &&
                          record.recruitmentProcess?.stage_type === 'HIRED' &&
                          record.recruitmentProcess?.status === 'ACTIVE'
                        }
                        onGiveUpClick={() => setGiveUpApplicant(record)}
                        sendAdditionalQuestions={Boolean(canManage && questionnaire.data?.count)}
                        onSendAdditionalQuestionsClick={() =>
                          setSendAdditionalQuestionsApplicants([record.id])
                        }
                        moveStage={
                          canManage &&
                          !(record.recruitmentProcess?.stage_type === 'HIRED') &&
                          jobPosting.data?.status === 'IN_PROGRESS' &&
                          record.recruitmentProcess?.status === 'ACTIVE'
                        }
                        onMoveStageClick={() => setChangeApplicantStage([record.id])}
                        reject={
                          canManage &&
                          !(record.recruitmentProcess?.stage_type === 'HIRED') &&
                          record.recruitmentProcess?.status === 'ACTIVE'
                        }
                        onRejectClick={() => setRejectApplicant([record.id])}
                        rollback={
                          canManage &&
                          !(record.recruitmentProcess?.stage_type === 'HIRED') &&
                          jobPosting.data?.status === 'IN_PROGRESS' &&
                          record.recruitmentProcess?.status !== 'ACTIVE'
                        }
                        onRollbackClick={() => setRollbackApplicant(record.id)}
                        select={canManage}
                        onSelectClick={() => setApplicantSelection([record.id])}
                        sendAnnotation
                        onSendAnnotationClick={() => setApplicantAnnotation(record.id)}
                        startInterview={canManage && isInterviewStage}
                        disabledInterviewTooltipText={
                          recruitmentProcessInterview?.status === 'FINISHED'
                            ? t('A entrevista já foi finalizada')
                            : ''
                        }
                        onStartInterviewClick={() => {
                          if (!recruitmentProcessInterview) {
                            return createInterview.mutate({
                              recruitmentProcessId: record.recruitmentProcess?.id as number,
                              stageId: record.stage?.id as number,
                            });
                          }

                          navigate({
                            pathname: '/:tenant/opportunities/:id/interview/:interview_id/',
                            params: {
                              tenant,
                              id,
                              interview_id: recruitmentProcessInterview.id,
                            },
                          });
                        }}
                        registerOpinion={Boolean(
                          canManage &&
                            isInterviewStage &&
                            recruitmentProcessInterview?.status === 'FINISHED'
                        )}
                        disabledOpinionText={
                          ['QUALIFIED', 'NOT_QUALIFIED'].includes(
                            recruitmentProcessInterview?.opinion_status || ''
                          )
                            ? t('O parecer já foi registrado')
                            : ''
                        }
                        onRegisterOpinionClick={() => {
                          navigate({
                            pathname: '/:tenant/opportunities/:id/interview/:interview_id/opinion/',
                            params: {
                              tenant,
                              id,
                              interview_id: recruitmentProcessInterview?.id,
                            },
                          });
                        }}
                        sendCustomTests={canManage && config.data?.enableAlchemerIntegration}
                        onSendCustomTestsClick={() => setSendCustomTestsApplicants([record.id])}
                        sendEmail={canManage}
                        onSendEmailClick={() => setSendEmailApplicants([record.id])}
                        sendTests={canManage}
                        onSendTestsClick={() => setSendTestsApplicants([record.id])}
                        markAsUnviewed
                        onMarkAsUnviewedClick={() =>
                          markAsUnviewedMutation.mutate({ id: record.recruiter_visualization_id })
                        }
                        sendIntegrationAdmission={
                          canManage &&
                          showIntegrationAdmissionFeatures &&
                          record.recruitmentProcess?.status === 'ACTIVE'
                        }
                        sendWhatsApp={canManage}
                        setSendIntegrationAdmission={() => setSendIntegrationAdmission(record.id)}
                        sendPracticalTechnicalCase={
                          currentStage?.sub_type === 'PRACTICAL_TECHNICAL_CASE'
                        }
                        onSendPracticalTechnicalCaseClick={() =>
                          setSendPracticalTechnicalCaseCandidates([record.id])
                        }
                      />
                    );
                  }}
                  width={1}
                  align="center"
                />
              </CandidateTable>
            </Space>
          </Card>
        </PageContent>
      </ErrorResult>
    </>
  );
};

export default withRouteChange()(ApplicantAnalysis);
